import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { NumericTextField } from '@/components';
import type { BushingTypeConfig, ParamName } from '@/types/bushing';
import type { ParamRange } from '@/api/trainingDataApi';

interface TargetGridProps {
  config: BushingTypeConfig;
  targets: Record<string, number | null>;
  amplitudes: Record<string, number | null>;
  linearTravel: Record<string, number | null>;
  onTargetChange: (param: string, value: number | null) => void;
  onAmplitudeChange: (param: string, value: number | null) => void;
  onLinearTravelChange: (key: string, value: number | null) => void;
  disabled?: boolean;
  errors?: Record<string, string>;
  /** Dynamic target stiffness ranges from training data. */
  targetRanges?: Record<string, ParamRange>;
}

/**
 * TargetGrid — Table-style layout with:
 *   Columns: [row label] | Target Stiffnesses | Test amplitude(±) | Linear Travel [mm] (C1/D1)
 *   Rows: one per stiffness parameter (Radial, Axial, Torsion, Conical, etc.)
 *
 *   Rules:
 *   - Conical params never have a test amplitude field.
 *   - C1: Linear Travel column with 1 input [mm] on the Axial row.
 *   - D1: Linear Travel column with 2 inputs (+/-) on the Radial Void row.
 */
const TargetGrid: React.FC<TargetGridProps> = ({
  config,
  targets,
  amplitudes,
  linearTravel,
  onTargetChange,
  onAmplitudeChange,
  onLinearTravelChange,
  disabled = false,
  errors = {},
  targetRanges = {},
}) => {
  const { paramNames, displayLabels, hasLinearTravel, linearTravelField, noAmplitudeParams } = config;

  const shortLabel = (param: ParamName): string => {
    const full = displayLabels[param] ?? param;
    const idx = full.indexOf('[');
    return idx > 0 ? full.substring(0, idx).trim() : full;
  };

  const unitLabel = (param: ParamName): string => {
    const full = displayLabels[param] ?? '';
    const match = full.match(/\[.*\]/);
    return match ? match[0] : '';
  };

  const showLinearTravel = hasLinearTravel && linearTravelField;
  const gridCols = showLinearTravel
    ? '80px 1fr 1fr 1fr'
    : '80px 1fr 1fr';

  const hideAmplitude = (param: ParamName): boolean =>
    noAmplitudeParams.includes(param);

  const isLinearTravelRow = (param: ParamName): boolean =>
    !!linearTravelField && linearTravelField.param === param;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Column headers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, gap: 1, mb: 0.5 }}>
        <Box />
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'center' }}>
          Target Stiffnesses
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'center' }}>
          Test amplitude(±)
        </Typography>
        {showLinearTravel && (
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'center' }}>
            Linear Travel [mm]
          </Typography>
        )}
      </Box>

      {/* Parameter rows */}
      {paramNames.map((param: ParamName) => (
        <Box
          key={param}
          sx={{ display: 'grid', gridTemplateColumns: gridCols, gap:1.5 , alignItems: 'center' }}
        >
          {/* Row label */}
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.secondary', lineHeight: 1.2,whiteSpace: 'nowrap'}}
          >
            {shortLabel(param)}
          </Typography>

          {/* Target stiffness */}
          <NumericTextField
            label={unitLabel(param)}
            value={targets[param] ?? null}
            onChange={(v) => onTargetChange(param, v)}
            disabled={disabled}
            error={!!errors[`target_${param}`]}
            helperText={errors[`target_${param}`]}
            min={targetRanges[param]?.min}
            max={targetRanges[param]?.max}
            size="small"
            fullWidth
          />

          {/* Test amplitude — hidden for conical params */}
          {hideAmplitude(param) ? (
            <Box />
          ) : (
            <NumericTextField
              label={unitLabel(param)}
              value={amplitudes[param] ?? null}
              onChange={(v) => onAmplitudeChange(param, v)}
              disabled={disabled}
              size="small"
              fullWidth
            />
          )}

          {/* Linear Travel column (C1/D1 only) */}
          {showLinearTravel && (
            <>
              {isLinearTravelRow(param) ? (
                linearTravelField!.boxes === 2 ? (
                  /* D1: two side-by-side fields with +/- labels */
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <NumericTextField
                      label="+"
                      value={linearTravel['radial_plus'] ?? null}
                      onChange={(v) => onLinearTravelChange('radial_plus', v)}
                      disabled={disabled}
                      size="small"
                      fullWidth
                    />
                    <NumericTextField
                      label="−"
                      value={linearTravel['radial_minus'] ?? null}
                      onChange={(v) => onLinearTravelChange('radial_minus', v)}
                      disabled={disabled}
                      size="small"
                      fullWidth
                    />
                  </Box>
                ) : (
                  /* C1: single field with [mm] label */
                  <NumericTextField
                    label="[mm]"
                    value={linearTravel['axial_c1'] ?? null}
                    onChange={(v) => onLinearTravelChange('axial_c1', v)}
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                )
              ) : (
                <Box />
              )}
            </>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default React.memo(TargetGrid);
